import React, { useState } from 'react'
import {
  AlertTriangle, BarChart2, CheckSquare, ChevronDown, ChevronUp,
  Download, Lock, Save, Share2,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../components/ui/PageHeader'
import { KPICard } from '../components/ui/KPICard'
import { useTenant } from '../hooks/useTenant'
import {
  useBusinessReviewSummary, useBusinessReviewTemplates,
  useSnapshots, useCreateSnapshot, useFreezeSnapshot,
} from '../hooks/useBusinessReview'
import { formatKpiEur } from '../lib/formatters'
import { downloadBlob } from '../lib/api'
import { WaterfallChart } from '../components/dashboard/WaterfallChart'
import { PipelineFunnelChart } from '../components/dashboard/PipelineFunnelChart'
import { ReadinessTable } from '../components/dashboard/ReadinessTable'
import { InsightBox } from '../components/dashboard/InsightBox'
import { FreezeModal } from '../components/dashboard/FreezeModal'
import { SnapshotHistory } from '../components/dashboard/SnapshotHistory'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function yearStart(): string {
  return `${new Date().getFullYear()}-01-01`
}

const MONTHS_IT = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']

export function buildPeriodLabel(start: string, end: string): string {
  const s = new Date(start)
  const e = new Date(end)
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${MONTHS_IT[s.getMonth()]} ${s.getFullYear()}`
  }
  if (s.getFullYear() === e.getFullYear()) {
    return `${MONTHS_IT[s.getMonth()]}–${MONTHS_IT[e.getMonth()]} ${s.getFullYear()}`
  }
  return `${MONTHS_IT[s.getMonth()]} ${s.getFullYear()} – ${MONTHS_IT[e.getMonth()]} ${e.getFullYear()}`
}

function fmtPct(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${v.toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

function fmtDays(v: number | null | undefined): string {
  if (v == null) return '—'
  return `${Math.round(v)}gg`
}

// ---------------------------------------------------------------------------
// Paywall
// ---------------------------------------------------------------------------

function GrowthPaywall() {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-6 text-center">
      <div className="rounded-full bg-indigo-500/10 p-5">
        <Lock className="w-10 h-10 text-indigo-400" />
      </div>
      <div>
        <h2 className="text-xl font-semibold text-[--km-text-primary] mb-2">
          Business Review — Growth+
        </h2>
        <p className="text-[--km-text-secondary] max-w-md">
          La Business Review CFO-grade è disponibile dal piano Growth.
          Consolida Strategic Snapshot, Pipeline, Order Readiness e Risk Engine in un unico report.
        </p>
      </div>
      <Link
        to="/upgrade"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-medium transition-colors"
      >
        Scopri il piano Growth
      </Link>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Data Confidence badge
// ---------------------------------------------------------------------------

function DataConfidenceBadge({ score, rag }: { score: number; rag: string }) {
  const colors: Record<string, string> = {
    high:   'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
    medium: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    low:    'bg-red-500/15 text-red-400 border-red-500/30',
  }
  const labels: Record<string, string> = { high: 'Alta', medium: 'Media', low: 'Bassa' }
  const cls = colors[rag] ?? colors.medium
  return (
    <span
      data-testid="confidence-badge"
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium ${cls}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      Dati: {labels[rag] ?? rag} ({Math.round(score)}%)
    </span>
  )
}

// ---------------------------------------------------------------------------
// Low confidence banner
// ---------------------------------------------------------------------------

function LowConfidenceBanner({ recommendation }: { recommendation: string }) {
  return (
    <div
      data-testid="low-confidence-banner"
      className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
      <span>
        <strong>Dati insufficienti per una review affidabile.</strong>{' '}
        {recommendation || 'Completa il caricamento dati nelle sezioni mancanti.'}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Layer accordion wrapper (collapsible on mobile)
// ---------------------------------------------------------------------------

function LayerSection({
  title,
  icon: Icon,
  children,
}: {
  title: string
  icon: React.ElementType
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(true)
  return (
    <section className="rounded-2xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] shadow-[var(--km-shadow-md)] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2.5 text-[--km-text-primary] font-semibold text-sm">
          <Icon className="w-4 h-4 text-indigo-400" />
          {title}
        </span>
        {open
          ? <ChevronUp className="w-4 h-4 text-[--km-text-secondary]" />
          : <ChevronDown className="w-4 h-4 text-[--km-text-secondary]" />
        }
      </button>
      {open && <div className="px-5 pb-5">{children}</div>}
    </section>
  )
}

// ---------------------------------------------------------------------------
// KPI grid
// ---------------------------------------------------------------------------

function KpiGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {children}
    </div>
  )
}


// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function BusinessReview() {
  const { data: tenant, isLoading: tenantLoading } = useTenant()
  const plan = tenant?.plan ?? 'trial'
  const isGrowthPlus = ['growth', 'enterprise'].includes(plan)

  // Periodo di default: 1 gen anno corrente → oggi
  const [periodStart, setPeriodStart] = useState(yearStart)
  const [periodEnd, setPeriodEnd] = useState(today)
  const [templateId, setTemplateId] = useState<string | null>(null)

  const { data: summary, isLoading: summaryLoading, error: summaryError } =
    useBusinessReviewSummary({
      period_start: periodStart,
      period_end: periodEnd,
      template_id: templateId,
    })

  const { data: templates } = useBusinessReviewTemplates()
  const { data: snapshots = [] } = useSnapshots(true)
  const createSnapshot = useCreateSnapshot()
  const freezeSnapshot = useFreezeSnapshot()

  const [showFreezeModal, setShowFreezeModal] = useState(false)
  const [isFreezing, setIsFreezing] = useState(false)
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null)
  const [pdfFormat, setPdfFormat] = useState<'a4' | 'landscape'>('a4')
  const [isExporting, setIsExporting] = useState(false)

  const canFreeze = summary != null && summary.data_confidence.overall_rag !== 'low'
  const canExportPdf = summary != null && !isExporting

  async function handleExportPdf() {
    if (!canExportPdf) return
    setIsExporting(true)
    try {
      const qs = new URLSearchParams({
        period_start: periodStart,
        period_end: periodEnd,
        fmt: pdfFormat,
      })
      if (templateId) qs.set('template_id', templateId)
      const filename = `business_review_${periodStart}_${periodEnd}.pdf`
      await downloadBlob(`/api/v1/business-review/export/pdf?${qs.toString()}`, filename)
    } finally {
      setIsExporting(false)
    }
  }

  async function handleFreeze() {
    if (!summary) return
    setIsFreezing(true)
    try {
      const snap = await createSnapshot.mutateAsync({
        period_label: buildPeriodLabel(periodStart, periodEnd),
        period_start: periodStart,
        period_end: periodEnd,
        kpi_data: summary as unknown as Record<string, unknown>,
        data_confidence: summary.data_confidence as unknown as Record<string, unknown>,
        template_id: templateId,
      })
      await freezeSnapshot.mutateAsync(snap.id)
      setShowFreezeModal(false)
    } finally {
      setIsFreezing(false)
    }
  }

  if (tenantLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Business Review"
        subtitle="Report CFO-grade: Strategic Snapshot, Pipeline, Order Readiness e Risk Engine"
      />

      {!isGrowthPlus && <GrowthPaywall />}

      {isGrowthPlus && (
        <>
          {/* ── Header controls ─────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Selettore periodo */}
            <div className="flex items-center gap-2 rounded-xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] px-3 py-2 text-sm">
              <label htmlFor="br-period-start" className="text-[--km-text-secondary] text-xs">Da</label>
              <input
                id="br-period-start"
                type="date"
                value={periodStart}
                onChange={e => setPeriodStart(e.target.value)}
                className="bg-transparent text-[--km-text-primary] text-xs focus:outline-none"
              />
              <span className="text-[--km-text-secondary] text-xs">–</span>
              <label htmlFor="br-period-end" className="sr-only">A</label>
              <input
                id="br-period-end"
                type="date"
                value={periodEnd}
                onChange={e => setPeriodEnd(e.target.value)}
                className="bg-transparent text-[--km-text-primary] text-xs focus:outline-none"
              />
            </div>

            {/* Selettore template */}
            <div className="rounded-xl border border-[var(--km-card-border)] bg-[var(--km-card-bg)] px-3 py-2">
              <label htmlFor="br-template" className="sr-only">Template</label>
              <select
                id="br-template"
                value={templateId ?? ''}
                onChange={e => setTemplateId(e.target.value || null)}
                className="bg-transparent text-[--km-text-primary] text-xs focus:outline-none"
                aria-label="Seleziona template"
              >
                <option value="">Template default</option>
                {templates?.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* Data Confidence badge */}
            {summary?.data_confidence && (
              <DataConfidenceBadge
                score={summary.data_confidence.overall_score}
                rag={summary.data_confidence.overall_rag}
              />
            )}

            {/* CTA */}
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                data-testid="save-snapshot-btn"
                onClick={() => setShowFreezeModal(true)}
                disabled={!canFreeze}
                title={!canFreeze ? 'Dati insufficienti (confidence bassa) — completa il caricamento prima di congelare' : 'Congela la review corrente'}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
                  canFreeze
                    ? 'border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/10'
                    : 'border-[var(--km-card-border)] text-[--km-text-secondary] opacity-40 cursor-not-allowed'
                }`}
              >
                <Save className="w-3.5 h-3.5" />
                Salva snapshot
              </button>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="export-pdf-btn"
                  onClick={handleExportPdf}
                  disabled={!canExportPdf}
                  title={!summary ? 'Carica i dati prima di esportare' : isExporting ? 'Esportazione in corso…' : 'Scarica PDF board-ready'}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
                    canExportPdf
                      ? 'border-indigo-500/50 text-indigo-300 hover:bg-indigo-500/10'
                      : 'border-[var(--km-card-border)] text-[--km-text-secondary] opacity-40 cursor-not-allowed'
                  }`}
                >
                  {isExporting
                    ? <div className="w-3.5 h-3.5 rounded-full border border-indigo-400 border-t-transparent animate-spin" />
                    : <Download className="w-3.5 h-3.5" />
                  }
                  Esporta PDF
                </button>
                <select
                  data-testid="pdf-format-select"
                  value={pdfFormat}
                  onChange={(e) => setPdfFormat(e.target.value as 'a4' | 'landscape')}
                  disabled={!canExportPdf}
                  className="h-[30px] rounded-lg border border-[var(--km-card-border)] bg-transparent px-1.5 text-[10px] text-[--km-text-secondary] disabled:opacity-40"
                  aria-label="Formato PDF"
                >
                  <option value="a4">A4</option>
                  <option value="landscape">16:9</option>
                </select>
              </div>
              <button
                type="button"
                disabled
                title="Disponibile in BR.3.8"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-[var(--km-card-border)] text-xs text-[--km-text-secondary] opacity-40 cursor-not-allowed"
              >
                <Share2 className="w-3.5 h-3.5" />
                Condividi
              </button>
            </div>
          </div>

          {/* ── Low confidence banner ────────────────────────────────── */}
          {summary?.data_confidence?.overall_rag === 'low' && (
            <LowConfidenceBanner recommendation={summary.data_confidence.recommendation} />
          )}

          {/* ── Loading / error ──────────────────────────────────────── */}
          {summaryLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
            </div>
          )}

          {summaryError && !summaryLoading && (
            <div
              data-testid="summary-error"
              className="flex items-center gap-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              Impossibile caricare la Business Review. Riprova tra qualche istante.
            </div>
          )}

          {/* ── Layer A — Strategic Snapshot ────────────────────────── */}
          {summary && (
            <LayerSection title="Layer A — Strategic Snapshot" icon={BarChart2}>
              <KpiGrid>
                <KPICard
                  label="Booked Revenue YTD"
                  value={summary.strategic.booked_revenue_ytd != null
                    ? formatKpiEur(summary.strategic.booked_revenue_ytd)
                    : '—'}
                  status={summary.strategic.attainment_pct != null && summary.strategic.attainment_pct >= 80
                    ? 'positive'
                    : summary.strategic.attainment_pct != null && summary.strategic.attainment_pct >= 50
                      ? 'warning'
                      : 'critical'}
                  subtext={summary.strategic.target_not_configured ? 'Target non configurato' : undefined}
                />
                <KPICard
                  label="Gap to Goal"
                  value={summary.strategic.gap_to_goal_value != null
                    ? formatKpiEur(summary.strategic.gap_to_goal_value)
                    : '—'}
                  delta={summary.strategic.gap_to_goal_pct != null
                    ? fmtPct(summary.strategic.gap_to_goal_pct)
                    : undefined}
                  deltaPositive={false}
                  status={summary.strategic.gap_to_goal_value != null && summary.strategic.gap_to_goal_value <= 0
                    ? 'positive'
                    : 'warning'}
                />
                <KPICard
                  label="Attainment %"
                  value={fmtPct(summary.strategic.attainment_pct)}
                  status={summary.strategic.attainment_pct != null && summary.strategic.attainment_pct >= 80
                    ? 'positive'
                    : summary.strategic.attainment_pct != null && summary.strategic.attainment_pct >= 50
                      ? 'warning'
                      : 'critical'}
                />
                <KPICard
                  label="YoY Growth"
                  value={fmtPct(summary.strategic.yoy_growth_pct)}
                  deltaPositive={summary.strategic.yoy_growth_pct != null && summary.strategic.yoy_growth_pct > 0}
                  status={summary.strategic.yoy_growth_pct != null && summary.strategic.yoy_growth_pct > 0
                    ? 'positive'
                    : 'warning'}
                />
              </KpiGrid>
              <WaterfallChart strategic={summary.strategic} />
            </LayerSection>
          )}

          {/* ── Layer B — Pipeline & Velocity ───────────────────────── */}
          {summary && (
            <LayerSection title="Layer B — Pipeline & Velocity" icon={BarChart2}>
              <KpiGrid>
                <KPICard
                  label="Pipeline pesata"
                  value={summary.pipeline.weighted_pipeline != null
                    ? formatKpiEur(summary.pipeline.weighted_pipeline)
                    : '—'}
                  status="neutral"
                />
                <KPICard
                  label="Coverage Ratio"
                  value={fmtPct(summary.pipeline.pipeline_coverage_ratio)}
                  status={summary.pipeline.pipeline_coverage_ratio != null
                    ? summary.pipeline.pipeline_coverage_ratio >= 100
                      ? 'positive'
                      : summary.pipeline.pipeline_coverage_ratio >= 70
                        ? 'warning'
                        : 'critical'
                    : 'neutral'}
                />
                <KPICard
                  label="Sales Velocity"
                  value={fmtDays(summary.pipeline.sales_velocity_days)}
                  subtext="giorni per chiusura"
                  status={summary.pipeline.sales_velocity_days != null
                    ? summary.pipeline.sales_velocity_days <= 30
                      ? 'positive'
                      : summary.pipeline.sales_velocity_days <= 60
                        ? 'warning'
                        : 'critical'
                    : 'neutral'}
                />
                <KPICard
                  label="Deal aged"
                  value={summary.pipeline.aged_deals_count != null
                    ? String(summary.pipeline.aged_deals_count)
                    : '—'}
                  subtext={summary.pipeline.aged_threshold_days
                    ? `>${summary.pipeline.aged_threshold_days}gg`
                    : undefined}
                  status={summary.pipeline.aged_deals_count != null && summary.pipeline.aged_deals_count > 3
                    ? 'critical'
                    : summary.pipeline.aged_deals_count != null && summary.pipeline.aged_deals_count > 0
                      ? 'warning'
                      : 'positive'}
                />
              </KpiGrid>
              <PipelineFunnelChart pipeline={summary.pipeline} />
            </LayerSection>
          )}

          {/* ── Layer C — Order Readiness ────────────────────────────── */}
          {summary && (
            <LayerSection title="Layer C — Order Readiness" icon={CheckSquare}>
              <KpiGrid>
                <KPICard
                  label="Backlog Conversion"
                  value={fmtPct(summary.readiness.backlog_conversion_pct)}
                  status={summary.readiness.backlog_conversion_pct != null
                    ? summary.readiness.backlog_conversion_pct >= 70
                      ? 'positive'
                      : summary.readiness.backlog_conversion_pct >= 40
                        ? 'warning'
                        : 'critical'
                    : 'neutral'}
                  subtext={summary.readiness.backlog_total_value != null
                    ? `Backlog: ${formatKpiEur(summary.readiness.backlog_total_value)}`
                    : undefined}
                />
                <KPICard
                  label="Efficiency Score"
                  value={summary.readiness.efficiency_score_available && summary.readiness.efficiency_score_days != null
                    ? fmtDays(summary.readiness.efficiency_score_days)
                    : '—'}
                  subtext={!summary.readiness.efficiency_score_available
                    ? 'Dati ore insufficienti'
                    : 'giorni medi avanzamento'}
                  status={summary.readiness.efficiency_score_available ? 'neutral' : 'warning'}
                />
              </KpiGrid>
              <ReadinessTable readiness={summary.readiness} />
            </LayerSection>
          )}

          {/* ── Insight Box (AI draft + management note) ─────────────── */}
          {summary && (
            <InsightBox
              summary={summary}
              periodStart={periodStart}
              periodEnd={periodEnd}
              onRefreshNarrative={() => {}}
            />
          )}

          {/* ── Storico snapshot congelati ───────────────────────────── */}
          <SnapshotHistory
            snapshots={snapshots}
            selectedId={selectedSnapshotId}
            onSelect={setSelectedSnapshotId}
          />
        </>
      )}

      {/* ── Freeze modal ─────────────────────────────────────────────── */}
      {showFreezeModal && (
        <FreezeModal
          periodLabel={buildPeriodLabel(periodStart, periodEnd)}
          onConfirm={handleFreeze}
          onCancel={() => setShowFreezeModal(false)}
          isLoading={isFreezing}
        />
      )}
    </div>
  )
}
